import * as XLSX from 'xlsx';
import type { MaterialsColor, MaterialsConfig, MaterialsPricing, MaterialsThickness } from '../pricing';

export const PRICING_MATRIX_COLUMNS = [
  { key: 'colorName', header: 'Cor' },
  { key: 'thicknessMm', header: 'Espessura (mm)' },
  { key: 'code', header: 'Código' },
  { key: 'price', header: 'Preço (R$/m²)' },
] as const;

export type PricingMatrixColumnKey = (typeof PRICING_MATRIX_COLUMNS)[number]['key'];

export const PRICING_MATRIX_HEADERS = PRICING_MATRIX_COLUMNS.map((column) => column.header);

export type PricingMatrixRow = {
  colorId: number | string;
  colorName: string;
  thicknessId: number | string;
  thicknessMm: number;
  code: string;
  price: number | null;
};

export type PricingMatrixImportEntry = {
  thicknessId: number | string;
  colorId: number | string;
  code: string;
  price: number;
};

export type PricingMatrixParseResult = {
  entries: PricingMatrixImportEntry[];
  errors: string[];
  skipped: number;
};

const COLUMN_KEY_TO_ROW_FIELD: Record<PricingMatrixColumnKey, keyof PricingMatrixRow> = {
  colorName: 'colorName',
  thicknessMm: 'thicknessMm',
  code: 'code',
  price: 'price',
};

const normalizeText = (value: unknown): string => String(value ?? '').trim();

const normalizeNumber = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const findPricingEntry = (
  pricing: MaterialsPricing[] | undefined,
  thicknessId: number | string,
  colorId: number | string,
): MaterialsPricing | undefined =>
  pricing?.find(
    (entry) => String(entry.thickness.id) === String(thicknessId) && String(entry.color.id) === String(colorId),
  );

export const buildPricingMatrixRows = (
  colors: MaterialsColor[] = [],
  thicknesses: MaterialsThickness[] = [],
  pricing: MaterialsPricing[] = [],
): PricingMatrixRow[] => {
  const sortedColors = [...colors].sort((a, b) => normalizeText(a.name).localeCompare(normalizeText(b.name), 'pt-BR'));
  const sortedThicknesses = [...thicknesses].sort((a, b) => Number(a.value) - Number(b.value));

  return sortedColors.flatMap((color) =>
    sortedThicknesses.map((thickness) => {
      const existing = findPricingEntry(pricing, thickness.id, color.id);
      return {
        colorId: color.id,
        colorName: normalizeText(color.name),
        thicknessId: thickness.id,
        thicknessMm: Number(thickness.value),
        code: normalizeText(existing?.code),
        price: existing?.price != null ? Number(existing.price) : null,
      };
    }),
  );
};

export const buildPricingMatrixRowsFromConfig = (config: MaterialsConfig | null | undefined): PricingMatrixRow[] =>
  buildPricingMatrixRows(config?.colors, config?.thicknesses, config?.pricing);

const pricingMatrixRowToCells = (row: PricingMatrixRow): (string | number)[] =>
  PRICING_MATRIX_COLUMNS.map((column) => {
    const field = COLUMN_KEY_TO_ROW_FIELD[column.key];
    const value = row[field];

    if (column.key === 'price') {
      return value == null ? '' : Number(value);
    }

    return value as string | number;
  });

export const formatPricingMatrixExcelBuffer = (
  rows: PricingMatrixRow[],
  sheetName = 'Matriz de preços',
): ArrayBuffer => {
  const worksheet = XLSX.utils.aoa_to_sheet([PRICING_MATRIX_HEADERS, ...rows.map(pricingMatrixRowToCells)]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  return XLSX.write(workbook, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
};

const buildHeaderIndexMap = (headers: string[]): Map<string, number> => {
  const map = new Map<string, number>();

  headers.forEach((header, index) => {
    const normalized = normalizeText(header).toLowerCase();
    map.set(normalized, index);
  });

  return map;
};

const getCellValue = (row: unknown[], index: number | undefined): unknown => {
  if (index == null || index < 0) return undefined;
  return row[index];
};

const findColor = (colors: MaterialsColor[], colorName: string): MaterialsColor | undefined => {
  const normalizedName = normalizeText(colorName).toLowerCase();
  return colors.find((color) => normalizeText(color.name).toLowerCase() === normalizedName);
};

const findThickness = (thicknesses: MaterialsThickness[], thicknessMm: number): MaterialsThickness | undefined =>
  thicknesses.find((thickness) => Number(thickness.value) === thicknessMm);

export const parsePricingMatrixSpreadsheet = (
  buffer: ArrayBuffer,
  config: MaterialsConfig,
): PricingMatrixParseResult => {
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];

  if (!sheetName) {
    return { entries: [], errors: ['Planilha vazia.'], skipped: 0 };
  }

  const worksheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json<unknown[]>(worksheet, { header: 1, defval: '' });

  if (!rows.length) {
    return { entries: [], errors: ['Planilha vazia.'], skipped: 0 };
  }

  const headerRow = rows[0].map((cell) => normalizeText(cell));
  const headerIndex = buildHeaderIndexMap(headerRow);
  const colorIndex = headerIndex.get('cor');
  const thicknessIndex = headerIndex.get('espessura (mm)');
  const codeIndex = headerIndex.get('código') ?? headerIndex.get('codigo');
  const priceIndex =
    headerIndex.get('preço (r$/m²)') ?? headerIndex.get('preco (r$/m²)') ?? headerIndex.get('preco (r$/m2)');

  const missingColumns: string[] = [];
  if (colorIndex == null) missingColumns.push('Cor');
  if (thicknessIndex == null) missingColumns.push('Espessura (mm)');
  if (codeIndex == null) missingColumns.push('Código');
  if (priceIndex == null) missingColumns.push('Preço (R$/m²)');

  if (missingColumns.length > 0) {
    return {
      entries: [],
      errors: [`Colunas obrigatórias ausentes: ${missingColumns.join(', ')}.`],
      skipped: 0,
    };
  }

  const colors = config.colors ?? [];
  const thicknesses = config.thicknesses ?? [];
  const entries: PricingMatrixImportEntry[] = [];
  const errors: string[] = [];
  let skipped = 0;

  rows.slice(1).forEach((row, index) => {
    const lineNumber = index + 2;
    const colorName = normalizeText(getCellValue(row, colorIndex));
    const thicknessMm = normalizeNumber(getCellValue(row, thicknessIndex));
    const code = normalizeText(getCellValue(row, codeIndex));
    const price = normalizeNumber(getCellValue(row, priceIndex));

    if (!colorName && thicknessMm == null && !code && price == null) {
      skipped += 1;
      return;
    }

    if (!colorName) {
      errors.push(`Linha ${lineNumber}: cor obrigatória.`);
      return;
    }

    if (thicknessMm == null) {
      errors.push(`Linha ${lineNumber}: espessura inválida.`);
      return;
    }

    const color = findColor(colors, colorName);
    if (!color) {
      errors.push(`Linha ${lineNumber}: cor "${colorName}" não encontrada.`);
      return;
    }

    const thickness = findThickness(thicknesses, thicknessMm);
    if (!thickness) {
      errors.push(`Linha ${lineNumber}: espessura ${thicknessMm} mm não encontrada.`);
      return;
    }

    if (price == null) {
      errors.push(`Linha ${lineNumber}: preço inválido.`);
      return;
    }

    entries.push({
      colorId: color.id,
      thicknessId: thickness.id,
      code,
      price,
    });
  });

  return { entries, errors, skipped };
};
